import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch } from "react-redux";
import {
  removeScenario,
  openDetailsDrawer,
} from "../../../Redux/promoSimulatorSlice/promoSimulatorSlice";
import { theme } from "../../../Theme";
import { PROMO_COLORS } from "../constants/colors";
import WarningBadges from "./WarningBadges";

function StatusChip({ status }) {
  const isWarning = status === "warning";
  const color = isWarning ? PROMO_COLORS.warning : PROMO_COLORS.safe;
  return (
    <Chip
      label={status}
      size="small"
      variant="outlined"
      sx={{
        borderColor: `${color}55`,
        backgroundColor: isWarning ? `${color}0d` : `${color}14`,
        color,
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    />
  );
}

const HEADERS = [
  "Name",
  "Brand",
  "SKUs",
  "Weeks",
  "Discount",
  "Uplift %",
  "ROI",
  "Warnings",
  "Status",
  "Actions",
];

export default function ScenarioTable({ scenarios = [] }) {
  const dispatch = useDispatch();

  if (!scenarios.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 5,
          px: 2,
          textAlign: "center",
          border: "1px dashed #D1D5DB",
          borderRadius: 2,
          bgcolor: "#FAFBFC",
        }}
      >
        <Typography color="text.secondary" sx={{ maxWidth: 360, mx: "auto", lineHeight: 1.6 }}>
          No scenarios yet. Create one in the Simulator tab.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: "1px solid #E8ECF0",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
      }}
    >
      <Table size="small" sx={{ "& .MuiTableCell-root": { borderColor: "#EEF1F4" } }}>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#F4F6F9",
              "& .MuiTableCell-root": { py: 1.25 },
            }}
          >
            {HEADERS.map((h) => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  letterSpacing: "0.02em",
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {scenarios.map((s) => (
            <TableRow
              key={s.id}
              hover
              sx={{
                transition: "background-color 0.15s ease",
                "&:hover": {
                  bgcolor: "rgba(25, 118, 210, 0.045)",
                },
                "&:last-of-type td": { borderBottom: 0 },
              }}
            >
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                {s.name}
              </TableCell>
              <TableCell sx={{ fontSize: 13 }}>{s.brand}</TableCell>
              <TableCell sx={{ fontSize: 13 }}>
                {Array.isArray(s.skus) ? s.skus.join(", ") : s.skus}
              </TableCell>
              <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                {typeof s.weeks?.week === "number"
                  ? `Week ${s.weeks.week}`
                  : (() => {
                      const a = s.weeks?.startWeek ?? s.weeks?.start;
                      const b = s.weeks?.endWeek ?? s.weeks?.end;
                      return a != null && b != null
                        ? `${a} → ${b}`
                        : "—";
                    })()}
              </TableCell>
              <TableCell sx={{ fontSize: 13 }}>{s.discountPct}%</TableCell>
              <TableCell sx={{ fontSize: 13 }}>
                {s.kpis?.upliftRatio != null
                  ? `${Number(s.kpis.upliftRatio).toFixed(2)}×`
                  : s.kpis?.upliftPct != null
                    ? `${s.kpis.upliftPct}%`
                    : "—"}
              </TableCell>
              <TableCell sx={{ fontSize: 13 }}>
                {s.kpis?.roi == null
                  ? "—"
                  : `${Number(s.kpis.roi).toFixed(2)}${
                      s.kpis?.upliftPct != null &&
                      s.kpis?.upliftRatio == null
                        ? "×"
                        : ""
                    }`}
              </TableCell>
              <TableCell sx={{ maxWidth: 140, verticalAlign: "middle" }}>
                {(s.warnings || []).length > 0 ? (
                  <WarningBadges warnings={s.warnings} compact />
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <StatusChip status={s.status || "active"} />
              </TableCell>
              <TableCell>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => dispatch(openDetailsDrawer(s.id))}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { bgcolor: "rgba(25, 118, 210, 0.12)" },
                      }}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => dispatch(removeScenario(s.id))}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { bgcolor: "rgba(209, 0, 0, 0.08)" },
                      }}
                    >
                      <DeleteOutlineIcon
                        fontSize="small"
                        sx={{ color: PROMO_COLORS.threat }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
